import React, { useState } from 'react';
import { Alert, AlertColor, Avatar, Box, Button, Container, Link, Snackbar, Stack, TextField, Tooltip, Typography } from '@mui/material';
import {
  authContainerStyling,
  authFormHeaderStyling,
  authFormPrimaryContainerStyling,
  authFormSecondaryContainerStyling,
  authHeaderContainerStyling,
  authHeaderTypographyStyling,
  authLogoStyling,
  authSecondaryContainerStyling,
  externalFormStyling,
} from '../../styles/sxStyling';
import { HelpOutline, LockOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import AuthLegalNotice from '../../components/common/AuthLegalNotice/AuthLegalNotice';
import { Loading } from '../../uikit';
import CustomAxios from '../../utility/customAxios';
import { useGetFormSubmissionById } from '../../services/react-query/form_submissions/useGetFormSubmissionById';
import { STATIC_ROUTES } from '../../routes/Routes';

type Response = {
  severity: AlertColor;
  message: string;
};

export default function FormAuthorizeEmail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [response, setResponse] = useState<Response | null>(null);
  const [email, setEmail] = useState<string>('');
  const { data: formSubmission } = useGetFormSubmissionById({ uuid });

  if (!formSubmission) return <Loading />;

  const handleSubmit = async () => {
    try {
      await CustomAxios.post(`form_submissions/${uuid}/reset_otp`, { email });
      navigate(`/form/authenticate/${uuid}`);
    } catch (err: any) {
      setResponse({ severity: 'error', message: err?.response?.data?.error });
    }
  };

  // TODO check open/close dates and don't show form if outside

  return (
    <Container component="main" maxWidth="md" sx={authContainerStyling}>
      <title>ScheduLink | Studio Management Software for Volume Photo Studios</title>
      <Box sx={authSecondaryContainerStyling}>
        <Box sx={authHeaderContainerStyling}>
          <Stack direction="column" spacing={2}>
            <Typography variant="h1" sx={authHeaderTypographyStyling}>
              Access your form securely.
            </Typography>
            <Typography variant="body1">Enter your email address and we&apos;ll send you a one-time password.</Typography>
          </Stack>
          {formSubmission.event_form_template.organization_logo_url ? (
            <Box
              component="img"
              src={formSubmission.event_form_template.organization_logo_url}
              alt={formSubmission.event_form_template.organization_name}
              sx={authLogoStyling}
            />
          ) : (
            <Avatar>{formSubmission.event_form_template.organization_name?.charAt(0)?.toUpperCase()}</Avatar>
          )}
        </Box>
        <Box sx={authFormPrimaryContainerStyling}>
          <Box sx={authFormSecondaryContainerStyling}>
            <Box sx={authFormHeaderStyling}>
              <Avatar sx={{ m: 1, bgcolor: 'primary.light' }}>
                <LockOutlined />
              </Avatar>
              <Typography variant="h5">Log In</Typography>
            </Box>
            <Box sx={externalFormStyling}>
              <Box sx={{ display: 'flex' }}>
                <TextField required autoFocus label="Email Address" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Tooltip title="Read more about why we need and how we use your email address." placement="right">
                  <Link href={STATIC_ROUTES.HELP_CENTER_URL} target="_blank" sx={{ alignContent: 'center', mx: 1 }}>
                    <HelpOutline sx={{ color: 'black' }} />
                  </Link>
                </Tooltip>
              </Box>
              <Box>
                <Button fullWidth onClick={handleSubmit} variant="contained" sx={{ mt: 3, mb: 2 }}>
                  Send One-Time Password
                </Button>
                <AuthLegalNotice />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Snackbar open={!!response} onClose={() => setResponse(null)}>
        <Alert severity={response?.severity} sx={{ width: '100%' }}>
          {response?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
